import React, { useEffect, useState } from "react";
import api from "../../api/client";
import { useAuth } from "../Data/AuthContext";

const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}

/**
 * Administrator profile.
 *
 * The previous version read "Admin Name" and "Major: Computer Science" out of
 * localStorage and never contacted the API, so nothing shown here reflected
 * the signed-in account. This reads and writes the real user record.
 */
export default function ProfilePage() {
  const { user, logout } = useAuth();

  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState("loading");
  const [loadError, setLoadError] = useState("");

  const [form, setForm] = useState({ firstname: "", lastname: "", bloodType: "" });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const [pw, setPw] = useState({ password: "", confirmPassword: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [showPwForm, setShowPwForm] = useState(false);

  useEffect(() => {
    if (!user?.email) return undefined;
    let cancelled = false;

    (async () => {
      setStatus("loading");
      try {
        const res = await api.get(`/users/getuserbyemail/${encodeURIComponent(user.email)}`);
        if (cancelled) return;
        const data = res.data?.user || res.data?.data || res.data;
        setProfile(data);
        setForm({
          firstname: data?.firstname || "",
          lastname: data?.lastname || "",
          bloodType: data?.bloodType || "",
        });
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setLoadError(err.response?.data?.message || "We couldn't load your profile.");
        setStatus("error");
      }
    })();

    return () => { cancelled = true; };
  }, [user?.email]);

  async function saveDetails(e) {
    e.preventDefault();
    setSaveMsg(null);

    if (!form.firstname.trim() || !form.lastname.trim()) {
      setSaveMsg({ kind: "error", text: "First and last name are both required." });
      return;
    }

    setSaving(true);
    try {
      await api.put(`/users/updateuserbyemail/${encodeURIComponent(user.email)}`, {
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        bloodType: form.bloodType,
      });
      setProfile((p) => ({ ...p, ...form }));
      setSaveMsg({ kind: "success", text: "Your details have been saved." });
    } catch (err) {
      setSaveMsg({
        kind: "error",
        text: err.response?.data?.message || "Could not save your changes.",
      });
    } finally {
      setSaving(false);
    }
  }

  async function changePassword(e) {
    e.preventDefault();
    setPwMsg(null);

    if (pw.password.length < 8) {
      setPwMsg({ kind: "error", text: "Password must be at least 8 characters." });
      return;
    }
    if (pw.password !== pw.confirmPassword) {
      setPwMsg({ kind: "error", text: "The two passwords do not match." });
      return;
    }

    setPwSaving(true);
    try {
      await api.put(`/users/updateuserbyemail/${encodeURIComponent(user.email)}`, {
        password: pw.password,
      });
      setPw({ password: "", confirmPassword: "" });
      setPwMsg({ kind: "success", text: "Password updated. You'll be signed out shortly." });
      // The stored hash has changed, so the current session no longer matches
      // the credentials behind it.
      setTimeout(() => logout(), 2500);
    } catch (err) {
      setPwMsg({
        kind: "error",
        text: err.response?.data?.message || "Could not update your password.",
      });
    } finally {
      setPwSaving(false);
    }
  }

  if (status === "loading") {
    return (
      <div aria-busy="true">
        <div className="es-card">
          <div className="es-card__body">
            {[0, 1, 2].map((i) => (
              <div key={i} className="es-skeleton es-skeleton--text" style={{ width: `${70 - i * 15}%` }} />
            ))}
          </div>
        </div>
        <span className="es-visually-hidden">Loading your profile</span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="es-alert es-alert--error" role="alert">
        {loadError}{" "}
        <button type="button" className="es-btn es-btn--ghost es-btn--sm"
          onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  const initials =
    `${(profile?.firstname || "")[0] || ""}${(profile?.lastname || "")[0] || ""}`.toUpperCase() || "?";

  return (
    <div className="es-rise">
      {/* identity header */}
      <section className="es-card" style={{ marginBottom: "1.5rem", overflow: "hidden" }}>
        <div style={{ background: "var(--es-gradient-deep)", height: "5.5rem" }} aria-hidden="true" />
        <div className="es-card__body" style={{ paddingTop: 0 }}>
          <div style={{ display: "flex", gap: "1.25rem", flexWrap: "wrap", alignItems: "flex-end", marginTop: "-2.25rem" }}>
            <span
              aria-hidden="true"
              style={{
                width: "4.5rem", height: "4.5rem", borderRadius: "50%",
                background: "var(--es-gradient-deep)", color: "#fff",
                display: "grid", placeItems: "center",
                fontSize: "1.4rem", fontWeight: 700,
                border: "3px solid var(--es-surface)", flex: "none",
              }}
            >
              {initials}
            </span>

            <div style={{ minWidth: 0, flex: "1 1 14rem", paddingBottom: "0.25rem" }}>
              <h2 className="es-h2" style={{ margin: 0, fontSize: "1.4rem" }}>
                {profile?.firstname} {profile?.lastname}
              </h2>
              <p className="es-muted" style={{ margin: "0.15rem 0 0", wordBreak: "break-all" }}>
                {profile?.email}
              </p>
            </div>

            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", paddingBottom: "0.35rem" }}>
              <span className="es-badge es-badge--neutral">
                {profile?.role === "admin" ? "Administrator" : "Member"}
              </span>
              <span className={`es-badge ${profile?.verified ? "es-badge--ok" : "es-badge--warn"}`}>
                {profile?.verified ? "Email verified" : "Not verified"}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div style={{
        display: "grid", gap: "1.5rem",
        gridTemplateColumns: "repeat(auto-fit, minmax(19rem, 1fr))",
        alignItems: "start",
      }}>
        {/* editable details */}
        <section className="es-card" aria-labelledby="pp-details">
          <div className="es-card__body">
            <h2 className="es-h2" id="pp-details" style={{ fontSize: "1.2rem" }}>Personal details</h2>

            {saveMsg && (
              <div className={`es-alert es-alert--${saveMsg.kind === "success" ? "success" : "error"}`}
                   role={saveMsg.kind === "success" ? "status" : "alert"}>
                {saveMsg.text}
              </div>
            )}

            <form onSubmit={saveDetails}>
              <div className="es-row">
                <div className="es-field">
                  <label className="es-label" htmlFor="pp-first">First name</label>
                  <input id="pp-first" className="es-input" value={form.firstname}
                    onChange={(e) => setForm((f) => ({ ...f, firstname: e.target.value }))} />
                </div>

                <div className="es-field">
                  <label className="es-label" htmlFor="pp-last">Last name</label>
                  <input id="pp-last" className="es-input" value={form.lastname}
                    onChange={(e) => setForm((f) => ({ ...f, lastname: e.target.value }))} />
                </div>
              </div>

              <div className="es-field">
                <label className="es-label" htmlFor="pp-blood">Blood type</label>
                <select id="pp-blood" className="es-select" value={form.bloodType}
                  onChange={(e) => setForm((f) => ({ ...f, bloodType: e.target.value }))}>
                  <option value="">Not specified</option>
                  {BLOOD_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>

              <button type="submit" className="es-btn es-btn--primary" disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </form>
          </div>
        </section>

        {/* account facts, read-only */}
        <section className="es-card" aria-labelledby="pp-account">
          <div className="es-card__body">
            <h2 className="es-h2" id="pp-account" style={{ fontSize: "1.2rem" }}>Account</h2>

            <table className="es-table" style={{ fontSize: "0.92rem" }}>
              <caption className="es-visually-hidden">Account information</caption>
              <tbody>
                <tr>
                  <th scope="row" style={{ background: "none", textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>
                    Email
                  </th>
                  <td style={{ wordBreak: "break-all" }}>{profile?.email}</td>
                </tr>
                <tr>
                  <th scope="row" style={{ background: "none", textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>
                    Role
                  </th>
                  <td>{profile?.role === "admin" ? "Administrator" : "Member"}</td>
                </tr>
                <tr>
                  <th scope="row" style={{ background: "none", textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>
                    Member since
                  </th>
                  <td>{formatDate(profile?.createdAt)}</td>
                </tr>
                <tr>
                  <th scope="row" style={{ background: "none", textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>
                    Last updated
                  </th>
                  <td>{formatDate(profile?.updatedAt)}</td>
                </tr>
              </tbody>
            </table>

            <p className="es-hint">
              Your email address and role can only be changed by another
              administrator.
            </p>
          </div>
        </section>

        {/* security */}
        <section className="es-card" aria-labelledby="pp-security">
          <div className="es-card__body">
            <h2 className="es-h2" id="pp-security" style={{ fontSize: "1.2rem" }}>Security</h2>

            {pwMsg && (
              <div className={`es-alert es-alert--${pwMsg.kind === "success" ? "success" : "error"}`}
                   role={pwMsg.kind === "success" ? "status" : "alert"}>
                {pwMsg.text}
              </div>
            )}

            {!showPwForm ? (
              <>
                <p className="es-body" style={{ fontSize: "0.92rem" }}>
                  Choose a new password if you think your current one may have
                  been seen by someone else. You&rsquo;ll be signed out afterwards.
                </p>
                <button type="button" className="es-btn es-btn--ghost"
                  style={{ marginTop: "1rem" }} onClick={() => setShowPwForm(true)}>
                  Change password
                </button>
              </>
            ) : (
              <form onSubmit={changePassword}>
                <div className="es-field">
                  <label className="es-label" htmlFor="pp-pw">New password</label>
                  <input id="pp-pw" type="password" className="es-input" autoComplete="new-password"
                    value={pw.password}
                    onChange={(e) => setPw((p) => ({ ...p, password: e.target.value }))} />
                  <p className="es-hint">At least 8 characters.</p>
                </div>

                <div className="es-field">
                  <label className="es-label" htmlFor="pp-pw2">Confirm new password</label>
                  <input id="pp-pw2" type="password" className="es-input" autoComplete="new-password"
                    value={pw.confirmPassword}
                    onChange={(e) => setPw((p) => ({ ...p, confirmPassword: e.target.value }))} />
                </div>

                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button type="submit" className="es-btn es-btn--primary" disabled={pwSaving}>
                    {pwSaving ? "Updating…" : "Update password"}
                  </button>
                  <button type="button" className="es-btn es-btn--ghost"
                    onClick={() => { setShowPwForm(false); setPw({ password: "", confirmPassword: "" }); setPwMsg(null); }}>
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
